import Alert from '@/components/Alert'
import TableView, { TableSection } from '@/components/TableView'
import { useIsAppReviewMode } from '@/lib/hooks/useIsAppReviewMode'
import { CurrentUser, fetchCurrentUserThunk } from '@/redux/slices/currentUserSlice'
import { useAppDispatch } from '@/redux/store'
import getBillingAccountPlanInfo from '@/redux/utils/getBillingAccountPlanInfo'
import { useState } from 'react'
import DestroyAccountAlert from './DestroyAccountAlert'

type UserInfoProps = {
  currentUser: CurrentUser
}

const ROLE_LABELS: Record<CurrentUser['role'], string> = {
  owner: '企业拥有者',
  admin: '企业管理员',
  worker: '企业协作者',
  outworker: '企业查看员',
}

export default function UserInfo({ currentUser }: UserInfoProps) {
  const dispatch = useAppDispatch()
  const isAppReviewMode = useIsAppReviewMode()

  const [refreshing, setRefreshing] = useState(false)
  const [destroyAccountAlertOpen, setDestroyAccountAlertOpen] = useState(false)

  const onRefresh = async () => {
    setRefreshing(true)
    await dispatch(fetchCurrentUserThunk())
    setRefreshing(false)
  }

  const canDestroyAccount = currentUser.accountDestroyable
  const needCheckWeixinAccount =
    currentUser.needResetPassword &&
    currentUser.authentications?.some(authentication => authentication.provider === 'wechat')

  const planInfo = getBillingAccountPlanInfo(currentUser.billingAccount)

  const sections: TableSection[] = [
    {
      title: ' ',
      rows: [
        {
          icon: currentUser.avatarUrl || 'person.circle',
          title: '头像',
        },
        {
          icon: 'person.text.rectangle',
          title: '姓名',
          detailText: currentUser.displayName,
        },
        {
          icon: 'phone',
          title: '电话',
          detailText: currentUser.mobile,
        },
        {
          icon: 'envelope',
          title: '邮箱',
          detailText: currentUser.email,
        },
      ],
    },
    ...(!isAppReviewMode
      ? [
          {
            title: '套餐信息',
            rows: [
              {
                icon: 'crown',
                title: '当前套餐',
                detailText: (() => {
                  if (planInfo.isActiveTrial) {
                    const trialTypeText = planInfo.isOrgPlanActiveTrial ? '企业' : '专业增强版'
                    return `${trialTypeText}试用中`
                  }
                  return currentUser.billingAccount.planPackage?.plan.label
                })(),
              },
              // 免费版不显示有效期
              ...(currentUser.billingAccount.planPackage?.plan.code !== 'free' &&
              currentUser.billingAccount.planPackage?.endDate
                ? [
                    {
                      icon: 'calendar',
                      title: '套餐有效期至',
                      detailText: currentUser.billingAccount.planPackage.endDate,
                    },
                  ]
                : []),
              ...(planInfo.isActiveTrial && planInfo.activeTrialEndDate
                ? [
                    {
                      icon: 'calendar',
                      title: '试用到期时间',
                      detailText: planInfo.activeTrialEndDate,
                    },
                  ]
                : []),
            ],
          },
          {
            title: '企业信息',
            rows: [
              {
                icon: 'building.2',
                title: '企业名称',
                detailText: currentUser.billingAccount.name,
              },
              {
                icon: 'person.badge.key',
                title: '企业身份',
                detailText: currentUser.role ? ROLE_LABELS[currentUser.role] : undefined,
              },
              {
                icon: 'globe',
                title: '二级域名',
                detailText: currentUser.billingAccount.subdomain,
              },
            ],
          },
        ]
      : []),
    {
      title: '',
      rows: [
        {
          icon: 'key',
          title: '注销账户',
          type: 'action',
          actionType: 'destructive',
          onPress: () => {
            if (!canDestroyAccount) {
              Alert.alert(
                '无法注销账户',
                '因检测到你的账号下存在企业成员，为保护企业数据资产，故该账号暂不能注销。如需注销请删除所有账户下的企业成员，如你不是企业管理员请联系对应企业的管理员进行操作。',
                [{ text: '我知道了', style: 'cancel' }]
              )
              return
            }

            if (needCheckWeixinAccount) {
              Alert.alert('请前往网页端注销', '此账号只关联的微信账号，需前往网页端微信扫码确认后才能进行注销操作。', [
                { text: '我知道了', style: 'cancel' },
              ])
              return
            }

            Alert.alert(
              '注销账户确认',
              '账号注销后，当前账号下的所有表单、数据将全部清除，所有的第三方服务和账号也将解除绑定关系，当前账号与其他账户共享的表单和数据也会被清除。由于该操作不可逆，请谨慎操作。',
              [
                { text: '取消', style: 'cancel' },
                { text: '确认注销', style: 'destructive', onPress: () => setDestroyAccountAlertOpen(true) },
              ]
            )
          },
        },
      ],
    },
  ]

  return (
    <>
      <TableView
        sections={sections}
        refreshing={refreshing}
        onRefresh={onRefresh}
        contentContainerStyle={{ paddingTop: 0 }}
      />
      <DestroyAccountAlert open={destroyAccountAlertOpen} setOpen={setDestroyAccountAlertOpen} />
    </>
  )
}
